/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  WorkflowGraphNode,
  WorkflowGraph,
  ActionWorkflowGraphNode,
} from 'cio/utils/visual-workflow-builder/workflow-graph';
import { action, computed } from '@ember/object';
import CampaignModel from 'cio/models/campaign';
import ActionModel from 'cio/models/action';
import { htmlSafe } from '@ember/template';
import { inject as service } from '@ember/service';
import { DropTargetItem, DragMode, DropType, VWBTab } from 'cio/utils/visual-workflow-builder/types';
import { Periods } from 'cio/utils/metrics/common';
import AuthService from 'cio/services/auth';
import { FlyDragEvent } from 'fly-drag-drop/utils/events';
import EventBusService, { VWB_BEFORE_ACTION_ADD_MOVE_OR_DELETE } from 'cio/services/event-bus';
import FlyDragDropManager from 'fly-drag-drop/services/fly-drag-drop-manager';
import { TimeRange } from 'cio/utils/time';
import AccountService from 'cio/services/account';

interface VisualWorkflowBuilderV2NodeArgs {
  workflowGraph: WorkflowGraph;
  node: WorkflowGraphNode;
  container: HTMLDivElement;
  campaign: CampaignModel;
  tab: VWBTab;
  metricsTabSelectedMeasures: string[];
  metricsTabTimePeriod: Periods;
  metricsTabTimeRange: TimeRange;
  onSelect: (action: ActionModel, event?: MouseEvent) => unknown;
  isSaving: boolean;
  isEditing: boolean;
  selectedAction: ActionModel | null;
  grayedOutActionIds: string[];
  onActionDragStart: (actionModel: ActionModel) => unknown;
  onActionDragEnd: () => unknown;
  highlightedNodeIds: string[];
  onActionClicked: (event: MouseEvent, action: ActionModel) => unknown;
  justAddedActionIds: string[];
  isSelectingDropAreaThroughClick: boolean;
  isCopyingHighlightedActions: boolean;
  isMovingHighlightedActions: boolean;
  canDrag?: boolean;
  canCopy: boolean;
  canMove: boolean;
  dragMode: DragMode;
  readOnly: boolean;
  isHighlighting: boolean;
  isMouseDown: boolean;
  hasMovedMouse: boolean;
  isShiftHeld: boolean;
  shouldAnimateNearbyActions: boolean;
  onCancelDragOperation: (isPerforminDragOperation: boolean) => unknown;
  onDropOrClickDropArea: (dropTargetItem: DropTargetItem, dropType: DropType, opts: any) => unknown;
  dragStartEvent: FlyDragEvent | null;
  trackInViewport: boolean;
}

let isExitOrIsParentBranchHighlighted = (
  campaign: CampaignModel | null,
  highlightedNodeIds: string[],
  node: ActionWorkflowGraphNode,
) => {
  // campaign can be null when navigating away from the VWB and the model gets
  // refreshed/unloaded before the VWB component gets destroyed (not 100% sure
  // why)
  let isParentBranchHighlighted = false;
  let parentBranchId = campaign?.parentBranchMap[node.id];

  if (parentBranchId) {
    isParentBranchHighlighted = highlightedNodeIds.includes(parentBranchId);
  }

  return node.action.isExit || isParentBranchHighlighted;
};

export default class VisualWorkflowBuilderV2Node extends Component<VisualWorkflowBuilderV2NodeArgs> {
  @service auth!: AuthService;
  @service eventBus!: EventBusService;
  @service flyDragDropManager!: FlyDragDropManager;
  @service account!: AccountService;

  get isGrayedOut() {
    return this.args.grayedOutActionIds.includes(this.args.node.id);
  }

  get shouldShowUnhighlightableTooltip() {
    if (this.args.node.typeCategory !== 'action') {
      return false;
    }

    return (
      this.args.isHighlighting &&
      (this.args.isShiftHeld || (this.args.isMouseDown && this.args.hasMovedMouse)) &&
      isExitOrIsParentBranchHighlighted(this.args.campaign, this.args.highlightedNodeIds, this.args.node)
    );
  }

  @computed(
    'args.node.type',
    'args.readOnly',
    'hasReadOnlyPermission',
    'args.isEditing',
    'args.highlightedNodeIds.[]',
    'args.dragMode',
    'args.canCopy',
    'args.canMove',
  )
  get canDrag() {
    if (this.args.node.typeCategory === 'virtual' || this.args.node.type === 'filter_match_delay_action') {
      return false;
    }

    let canDrag =
      (this.args.canDrag ?? true) && !this.args.readOnly && !this.hasReadOnlyPermission && !this.args.isEditing;

    if (!canDrag) {
      return canDrag;
    }

    if (this.args.dragMode === DragMode.Move && !this.args.canMove) {
      return false;
    }

    if (this.args.dragMode === DragMode.Copy && !this.args.canCopy) {
      return false;
    }

    if ((this.args.highlightedNodeIds ?? []).length > 0 && this.args.highlightedNodeIds.includes(this.args.node.id)) {
      return canDrag;
    }

    // If the user is dragging a collection action and they don't have collections in PlanFeatures, don't allow them to copy

    if (
      canDrag &&
      this.args.dragMode === DragMode.Copy &&
      this.args.node.action.isCollectionAction &&
      this.account.planFeatures.collections.enabled
    ) {
      return false;
    }

    return !this.args.node.action.isExit;
  }

  @computed('args.highlightedNodeIds.[]')
  get isHighlighted() {
    return this.args.highlightedNodeIds.includes(this.args.node.id);
  }

  get hasJustBeenAdded() {
    return this.args.justAddedActionIds.includes(this.args.node.id);
  }

  get hasReadOnlyPermission() {
    if (this.args.campaign.isApiTriggered === true) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  constructor(owner: unknown, args: VisualWorkflowBuilderV2NodeArgs) {
    super(owner, args);

    this.eventBus.subscribe(VWB_BEFORE_ACTION_ADD_MOVE_OR_DELETE, this, this.onAddMoveOrDelete);
  }

  willDestroy() {
    super.willDestroy();

    this.eventBus.unsubscribe(VWB_BEFORE_ACTION_ADD_MOVE_OR_DELETE, this, this.onAddMoveOrDelete);
  }

  @action
  onAddMoveOrDelete() {
    // If an action (or multiple) is about to be added, moved or deleted, hide
    // all "visible" nodes that are out of the viewport, to make rerendering the
    // VWB snappier
    if (this.args.trackInViewport && this._isVisible && !this.inViewport) {
      this._isVisible = false;
    }
  }

  get isVisible() {
    return this.args.trackInViewport ? this._isVisible : true;
  }
  // isvisible tells us whether the node should render, while inviewport tells
  // us if it's actually in the viewport. once an item exits the viewport, we
  // want it to stay visible
  @tracked _isVisible = false;
  @tracked inViewport = false;

  @action
  onEnterViewport() {
    this.inViewport = true;
    this._isVisible = true;
  }

  @action
  onExitViewport() {
    this.inViewport = false;
  }

  @computed('args.node.{width,height,x,y,translateX,translateY}')
  get style() {
    return htmlSafe(`
--nodeHeight: ${this.args.node.height}px;
--nodeWidth: ${this.args.node.width}px;
--nodeXCoord: ${this.args.node.x}px;
--nodeYCoord: ${this.args.node.y}px;
--translateX: ${this.args.node.translateX ?? 0}px;
--translateY: ${this.args.node.translateY ?? 0}px;
`);
  }

  @computed('args.node.action.isSplitTest', 'args.node.action.info.group')
  get groupTypeClass() {
    if (this.args.node.type === 'filter_match_delay_action' || this.args.node.type === 'virtual_enter') {
      return 'vwb-workflow-action--trigger';
    }

    if (this.args.node.type === 'exit_action') {
      return 'vwb-workflow-action--exit';
    }

    if (this.args.node.typeCategory === 'virtual' || !this.args.node.action) {
      return '';
    }

    if (this.args.node.action.isSplitTest) {
      return 'vwb-workflow-action--split';
    }

    return `vwb-workflow-action--${this.args.node.action.info.group ?? 'default'}`;
  }

  get parentBranch() {
    let branchId = this.args.campaign?.parentBranchMap?.[this.args.node.id];

    if (branchId != null) {
      return this.args.campaign.branchesMap?.[branchId];
    }

    return null;
  }

  get exitNodePathIndex() {
    if (this.args.node.typeCategory === 'action' && this.args.node.action?.isExit && this.parentBranch) {
      let edge = (this.parentBranch as any).edges.find((e: any) => e.nodes.mapBy('id').includes(this.args.node.id));

      return edge.edge.index;
    }

    return null;
  }
}
